import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';
import api from '../config/api';
import './common.css';
import './Dashboard.css';

function Users() {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        // Redirect non-admin users
        if (!isAdmin()) {
            navigate('/products');
            return;
        }
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/api/users');
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            setError('Failed to fetch users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await api.delete(`/api/users/${userId}`);
            if (response.data.success) {
                setSuccess('User deleted successfully');
                fetchUsers();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete user');
            setTimeout(() => setError(''), 3000);
        }
    };

    const openPasswordModal = (u) => {
        setSelectedUser(u);
        setNewPassword('');
        setShowPasswordModal(true);
        setError('');
        setSuccess('');
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        try {
            const response = await api.put(`/api/users/${selectedUser.id}/password`, {
                newPassword
            });

            if (response.data.success) {
                setSuccess('Password changed successfully');
                setShowPasswordModal(false);
                setNewPassword('');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to change password');
        }
    };

    const handleToggleActive = async (userId, userName, isActive) => {
        const action = isActive ? 'deactivate' : 'activate';
        const confirmed = window.confirm(
            `Are you sure you want to ${action} user "${userName}"?`
        );

        if (!confirmed) return;

        try {
            const response = await api.put(`/api/users/${userId}/toggle-active`);
            if (response.data.success) {
                setSuccess(response.data.message);
                fetchUsers();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || `Failed to ${action} user`);
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="page-container">
            <Navigation />

            <div className="page-content">
                <div className="page-header">
                    <h2>User Management</h2>
                    <div className="header-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/add-user')}
                        >
                            + Add New User
                        </button>
                    </div>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {loading ? (
                    <div className="loading">Loading users...</div>
                ) : (
                    <div className="table-wrapper">
                        <table className="data-table users-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id}>
                                        <td>{u.id}</td>
                                        <td>{u.firstName || u.first_name} {u.lastName || u.last_name}</td>
                                        <td>{u.email}</td>
                                        <td>
                                            <span className={`role-badge ${u.role}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${(u.isActive ?? u.is_active) ? 'active' : 'inactive'}`}>
                                                {(u.isActive ?? u.is_active) ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>{formatDate(u.createdAt || u.created_at)}</td>
                                        <td className="actions-cell">
                                            <button
                                                className={`btn btn-small ${(u.isActive ?? u.is_active) ? 'btn-warning' : 'btn-success'}`}
                                                onClick={() => handleToggleActive(u.id, `${u.firstName || u.first_name} ${u.lastName || u.last_name}`, (u.isActive ?? u.is_active))}
                                                disabled={u.id === user?.id}
                                                title={(u.isActive ?? u.is_active) ? 'Deactivate User' : 'Activate User'}
                                            >
                                                {(u.isActive ?? u.is_active) ? '🚫 Deactivate' : '✅ Activate'}
                                            </button>
                                            <button
                                                className="btn btn-small btn-password"
                                                onClick={() => openPasswordModal(u)}
                                                title="Change Password"
                                            >
                                                🔑 Password
                                            </button>
                                            <button
                                                className="btn btn-small btn-danger"
                                                onClick={() => handleDeleteUser(u.id, `${u.firstName || u.first_name} ${u.lastName || u.last_name}`)}
                                                disabled={u.id === user?.id}
                                                title={u.id === user?.id ? "Cannot delete yourself" : "Delete User"}
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {users.length === 0 && (
                            <div className="no-users">
                                No users found.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Change Password</h3>
                        <p className="modal-subtitle">
                            User: {selectedUser?.firstName || selectedUser?.first_name} {selectedUser?.lastName || selectedUser?.last_name}
                        </p>

                        {error && <div className="error-message">{error}</div>}

                        <form onSubmit={handleChangePassword}>
                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Minimum 6 characters"
                                    minLength="6"
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="btn btn-primary">
                                    Change Password
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowPasswordModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Users;
